import { CONTRACTING_SOURCE_APPLICATION } from '@makemydeal/dr-dash-bff-types';
import type { FieldConfig, EntityConfig, ConfigMap } from '@makemydeal/dr-shared-ui-utils';
import memoize from 'lodash/memoize';
import { getContractFieldValidationConfig } from './contractFieldErrorConfigs';
import { CONTRACT_API_ERRORED_VPP, CONTRACT_API_ERRORED_VEHICLE_INSURANCE } from '@makemydeal/dr-shared-types';

const eventNamesWithoutSourceApplication = ['Contracting:Cancelled'];
const verificationEventNames = [
    'ContractingDecisions:Verified',
    'ContractingDecisions:VerificationFailed',
    'ContractingDecisions:VerifiedWithWarnings'
];

/**
 *
 * @param eventTimeString
 * @returns Time in milliseconds since epoch or undefined if the input is falsy
 */
export const parseEventTime = (eventTimeString: string) => {
    if (!eventTimeString) {
        return undefined;
    }
    return Date.parse(`${eventTimeString}Z`);
};

/**
 *
 * @param timeInMs - The time in milliseconds
 * @returns
 */
export const getSecondsSafe = (timeInMs: number | undefined | null) => {
    if (!timeInMs) {
        return 0;
    }
    return timeInMs / 1000;
};

/**
 *
 * @param startTimeMs - The start time in milliseconds
 * @returns The elapsed time in seconds, 0 if startTimeMs is undefined or null
 */
export const getElapsedTimeInSeconds = (startTimeMs: number | undefined | null) => {
    if (!startTimeMs) {
        return 0;
    }
    return getSecondsSafe(Date.now() - startTimeMs);
};

/**
 *
 * @param startTimeMs The start time in milliseconds
 * @param endTimeMs The end time in milliseconds
 * @returns The duration in seconds, or undefined if either startTimeMs or endTimeMs is falsy
 */
export const getDurationInSeconds = (startTimeMs: number | null, endTimeMs: number | null): number | undefined => {
    if (!startTimeMs || !endTimeMs) {
        return undefined;
    }
    return getSecondsSafe(endTimeMs - startTimeMs);
};

/**
 * Checks for a specific error message representing a case where MV needs to try to cancel contracts before generating them again
 * @param eventPayload the DT Event payload
 * @returns
 */
export const shouldCancelContracts = (eventPayload: any): boolean => {
    const eventName = eventPayload.eventName;
    const errors = eventPayload.payload?.message?.errors;
    return (
        eventName === 'Contracting:SaveFailed' &&
        Array.isArray(errors) &&
        errors.some((error: any) => error.message?.toLowerCase() === 'invalid state transition detected for a deal')
    );
};

/**
 * Checks if the error is due to deal being associated with a different lender
 * This error requires canceling the existing contract and retrying
 * @param eventPayload the DT Event payload
 * @returns true if the error is about different lender association
 */
export const shouldCancelForDifferentLender = (eventPayload: any): boolean => {
    const eventName = eventPayload.eventName;
    const errors = eventPayload.payload?.message?.errors;
    return (
        eventName === 'Contracting:SaveFailed' &&
        Array.isArray(errors) &&
        errors.some((error: any) => error.message?.toLowerCase().includes('already associated with a different lender'))
    );
};

export const checkIfSpotError = (eventPayload: any): boolean => {
    const eventName = eventPayload.eventName;
    const errors = eventPayload.payload?.message?.errors;
    return (
        eventName === 'Contracting:SaveFailed' &&
        Array.isArray(errors) &&
        errors.some(
            (error: any) =>
                error.message
                    ?.toLowerCase()
                    .includes(
                        'a contract could not be created for this deal. the following is not a supported configuration in this dealership'
                    ) && error.message.toLowerCase().includes('spot')
        )
    );
};
export const isVerifiedEvent = (eventName: string): boolean => verificationEventNames.includes(eventName);

/**
 * Validates a contracting event based on correlation ID and source application.
 *
 * @param enableMVContractingSourceAppHeader - Feature toggle to enable source application validation
 * @param currentCorrelationId - The expected correlation ID for the current contracting session
 * @param eventName - The name of the contracting event
 * @param eventKeyData - The event data containing correlationId and sourceApplication
 * @returns true if the event is valid and should be processed, false otherwise
 */
export const validateEvent = (
    enableMVContractingSourceAppHeader: boolean,
    currentCorrelationId: string,
    eventName: string,
    eventKeyData?: any
): boolean => {
    if (!eventKeyData) {
        // eslint-disable-next-line no-console
        console.debug('Contracting: Event Key Data is missing', { eventName });
        return false;
    }
    const eventCorrelationId = eventKeyData.correlationId;
    const eventSourceApplication = eventKeyData.sourceApplication;

    if (
        // the Contracting:Cancelled event will not have a source application, so we skip the validation. Using an array for exstensibility
        !eventNamesWithoutSourceApplication.includes(eventName) &&
        enableMVContractingSourceAppHeader &&
        eventSourceApplication?.toUpperCase() !== CONTRACTING_SOURCE_APPLICATION
    ) {
        return false;
    }
    if (eventCorrelationId !== currentCorrelationId && !isVerifiedEvent(eventName)) {
        return false;
    }

    // Event is valid - should be processed
    return true;
};

export const parseContractErrorMessages = (obj: any): string => {
    const reasonItems: string[] = [];
    for (const [k, v] of Object.entries(obj)) {
        if (Array.isArray(v)) {
            reasonItems.push(`${k} - ${v.join('; ')}`);
        } else if (typeof v === 'object' && v !== null) {
            reasonItems.push(`${k} - ${JSON.stringify(v)}`);
        } else {
            reasonItems.push(`${k} - ${v}`);
        }
    }
    return reasonItems.join(', ');
};

export const normalizeContractErrors = (errors: any): string[] => {
    const reasons: string[] = [];
    if (!errors) {
        return reasons;
    }

    for (const [key, values] of Object.entries(errors)) {
        if (Array.isArray(values)) {
            for (const val of values) {
                if (typeof val === 'string') {
                    reasons.push(`${key}: ${val}`);
                } else if (typeof val === 'object' && val !== null) {
                    reasons.push(`${key}: ${parseContractErrorMessages(val)}`);
                }
            }
        } else if (typeof values === 'string') {
            reasons.push(`${key}: ${values}`);
        } else if (typeof values === 'object' && values !== null) {
            reasons.push(`${key}: ${parseContractErrorMessages(values)}`);
        }
    }
    return reasons;
};

/**
 * Matches the lender's vehicle value mismatch reason
 */
const VEHICLE_VALUE_MISMATCH_REGEX =
    /vehicle value amount:\s*\$?([\d,]+(?:\.\d+)?)\s*is different than approval:\s*\$?([\d,]+(?:\.\d+)?)/i;

/**
 * Parses the amounts out of the lender's vehicle value mismatch reason so MV can surface an
 * actionable message pointing the dealer at Vehicle Details > Invoice.
 * @param reason a single contract failure reason
 * @returns the vehicle value and lender-approved amounts, or undefined when the reason is a different error
 */
export const parseVehicleValueMismatchReason = (reason: string): { vehicleValue: number; approvedAmount: number } | undefined => {
    if (typeof reason !== 'string') {
        return undefined;
    }

    const match = reason.match(VEHICLE_VALUE_MISMATCH_REGEX);
    if (!match) {
        return undefined;
    }

    // Both captures are constrained to digits, commas and an optional decimal, so Number() is always safe here.
    return {
        vehicleValue: Number(match[1].replace(/,/g, '')),
        approvedAmount: Number(match[2].replace(/,/g, ''))
    };
};

export const placeholderTextMapping: Record<string, string> = {
    buyer: 'Buyer',
    coBuyer: 'Co-Buyer',
    vehicleInsurance: 'Insurance',
    lender: 'Lender',
    accessories: 'Accessory',
    tradeIn: 'Trade In',
    tradeIns: 'Trade In',
    gap: 'Protection',
    [CONTRACT_API_ERRORED_VPP]: 'Protection',
    [CONTRACT_API_ERRORED_VEHICLE_INSURANCE]: 'Insurance',
    vehicle: 'Vehicle'
};

const getContractEntityConfigMap = (entity: string, preferInlineMessage: boolean): EntityConfig => {
    const FIELD_PATH_SEPARATOR = '.';

    const contractConfig = {
        ...getContractFieldValidationConfig(preferInlineMessage)
    };

    const entityConfigMap = {} as ConfigMap;
    Object.entries(contractConfig).forEach(([fieldPath, fieldConfig]) => {
        const [entityName, fieldName] = fieldPath.split(FIELD_PATH_SEPARATOR);

        if (!fieldName) {
            entityConfigMap[entityName] = fieldConfig;
        }

        if (entityName && fieldName) {
            entityConfigMap[entityName] = entityConfigMap[entityName] || {};
            (entityConfigMap[entityName] as Record<string, FieldConfig>)[fieldName] = fieldConfig;
        }
    });

    return entity ? entityConfigMap[entity] : entityConfigMap;
};

export const createContractEntityConfigMap = () => {
    return memoize(
        getContractEntityConfigMap,
        (entity: string, preferInlineMessage: boolean) => `${entity || 'all'}-${preferInlineMessage}`
    );
};

export const shouldCancelVerification = (payload: any): boolean => {
    const errors = payload?.payload?.message?.errors;
    if (!errors) {
        return false;
    }

    const errorKeys = [
        'missing_required_lender_fields',
        'missing_required_lender_fields_not_mapped',
        'validation_errors',
        'validation_errors_not_mapped',
        'validations',
        'validations_not_mapped',
        'complexvalidations',
        'charge_count_exceeds_max'
    ] as const;

    return errorKeys.some((key) => {
        const errorObj = errors[key];
        return errorObj && typeof errorObj === 'object' && Object.keys(errorObj).length > 0;
    });
};

export const TRADE_IN_ERROR_KEYS = [
    'vehicle.year',
    'vehicle.make',
    'vehicle.model',
    'vehicle.trim',
    'lienholder.name',
    'lienholder.accountNumber',
    'lienholder.streetAddress',
    'lienholder.city',
    'lienholder.state',
    'lienholder.zip'
] as const;

// Converts a nested error object into a flat dot-notation error map
export const flattenNestedErrors = (nestedErrors: Record<string, any>): Record<string, boolean> => {
    const flatErrors: Record<string, boolean> = {};

    for (const key of TRADE_IN_ERROR_KEYS) {
        const [category, field] = key.split('.');
        if (nestedErrors?.[category]?.[field]) {
            flatErrors[key] = true;
        }
    }

    return flatErrors;
};

// Recursively flattens a nested error-flag object into a flat dot-notation map of booleans.
// Unlike flattenNestedErrors, this handles arbitrary nesting depth (e.g. collection indexes).
// Returns null when input is null/undefined, enabling clean setOrClear patterns.
export const flattenNestedErrorFlags = (
    obj: Record<string, any> | null | undefined,
    prefix = ''
): Record<string, boolean> | null => {
    if (!obj) return null;
    const result: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'boolean') {
            result[fullKey] = value;
        } else if (typeof value === 'object' && value !== null) {
            const nested = flattenNestedErrorFlags(value, fullKey);
            if (nested) Object.assign(result, nested);
        }
    }
    return Object.keys(result).length > 0 ? result : null;
};

// Flatten leaf error objects to a single property while preserving structure.
// Pass 'errorMessage' to get strings, or 'hasError' to get booleans.
export function flattenLeaf(node: any, prop: 'errorMessage' | 'hasError'): any {
    if (node == null) return node;
    if (Array.isArray(node)) {
        return node.map((child) => flattenLeaf(child, prop));
    }
    if (typeof node === 'object') {
        if ('hasError' in node && typeof (node as any).hasError === 'boolean') {
            if (prop === 'errorMessage') {
                return (node as any).errorMessage ?? '';
            }
            // prop === 'hasError'
            return Boolean((node as any).hasError);
        }
        const out: any = {};
        for (const key of Object.keys(node)) {
            out[key] = flattenLeaf((node as any)[key], prop);
        }
        return out;
    }
    return node;
}
